// SearchBar.js
// Componente padronizado de busca usado em toda a aplicação.
// Substitui a renderização in-place do MainContainer.renderSimpleSearch e
// variantes — e será consumido diretamente pelas pages na F5 (drop MainContainer).
//
// Padrão visual (decisão 2026-05-10, Filipe — princípio Jobs uniformity):
//   - TextField outlined (theme.js já define height 44, radius sm via shape.borderRadius)
//   - SearchIcon como startAdornment (text.disabled)
//   - Clear button quando há valor (endAdornment)
//   - Voice button opcional (endAdornment)
//   - placeholder em vez de label flutuante — mais limpo, não consome altura extra
//   - fullWidth por default
//
// Props:
//   value, onChange — controlled
//   onClear — opcional, mostra X quando há valor
//   onVoiceClick — opcional, mostra mic icon
//   placeholder — default 'Buscar'
//   autoFocus — default false
//   id/name — default 'busca' (compat com MainContainer existente)

import React, { useRef, useEffect } from 'react'
import { TextField, InputAdornment, IconButton } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import MicIcon from '@mui/icons-material/Mic'

export default function SearchBar({
  value,
  onChange,
  onClear,
  onVoiceClick,
  placeholder = 'Buscar',
  autoFocus = false,
  id = 'busca',
  name = 'busca',
  fullWidth = true,
  sx,
}) {
  const inputRef = useRef(null)

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [autoFocus])

  // Suporta uso controlled (value passado) E uncontrolled (value omitido).
  // Pages legadas frequentemente fazem o filter na callback handleSearch sem
  // manter state local do input — nesse caso passam onChange mas omitem value
  // e a TextField vira uncontrolled (defaultValue '').
  const isControlled = value !== undefined
  const hasValue = isControlled && !!value && String(value).length > 0
  const controlProps = isControlled ? { value } : { defaultValue: '' }

  return (
    <TextField
      inputRef={inputRef}
      id={id}
      name={name}
      type='text'
      {...controlProps}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete='off'
      fullWidth={fullWidth}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon fontSize='small' sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
          endAdornment:
            (hasValue && onClear) || onVoiceClick ? (
              <InputAdornment position='end'>
                {hasValue && onClear && (
                  <IconButton
                    size='small'
                    onClick={onClear}
                    aria-label='Limpar busca'
                    edge='end'
                    sx={{ mr: onVoiceClick ? 0.5 : 0 }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                )}
                {onVoiceClick && (
                  <IconButton
                    size='small'
                    onClick={onVoiceClick}
                    aria-label='Buscar por voz'
                    edge='end'
                  >
                    <MicIcon fontSize='small' />
                  </IconButton>
                )}
              </InputAdornment>
            ) : null,
        },
      }}
      sx={sx}
    />
  )
}
